/**
 * DorisSeriesProvider — historical data from Doris (OLAP).
 *
 * On open with a window range it queries `PC.sensor_history` (hourly avg) and
 * pushes one reset patch; `loadDetail` pulls `PC.sensor_history_detail` (full
 * resolution) for a zoom window. Sensor→channel mapping uses the ChannelSpec
 * `doris` matchers (with axis scoping for edge-16-style multi-axis components).
 */

import { orgNatsClient, StatementIds } from '../../services/OrgNATSClient';
import { getValidToken } from '../../auth/tokenManager';
import { resolveSensorChannel, dorisPrefixes } from '../channelResolver';
import { JETSTREAM_OP_TIMEOUT_MS } from '../../utils/natsConfig';
import type {
  ProviderContext,
  SeriesProvider,
  SeriesPatch,
  SeriesPatchHandler,
  SeriesPoint,
  SeriesSubscription,
} from './SeriesProvider';
import type { ResolvedRange } from '../spec';

interface SensorHistoryRow {
  timestamp: string;
  sensor_name: string;
  avg_value: number;
}
interface SensorDetailRow {
  timestamp: string;
  sensor_name: string;
  value: number;
}

async function query<T>(statement: string, params: Record<string, unknown>): Promise<T[]> {
  const token = await getValidToken();
  const res = await orgNatsClient.requestChunked<T>(statement, params, token, { timeout: JETSTREAM_OP_TIMEOUT_MS });
  return res.success && res.data ? res.data : [];
}

function group(
  rows: Array<{ timestamp: string; sensor_name: string }>,
  value: (r: any) => number,
  ctx: ProviderContext,
): SeriesPatch {
  // Dedupe by timestamp per channel: the raw `measurement` table can hold
  // several rows for the same (sensor, timestamp) that differ only by rounding.
  // Collapse them (last value wins) so a channel never double-plots a point.
  const byTs: Record<string, Map<number, number>> = {};
  for (const row of rows) {
    const channel = resolveSensorChannel(row.sensor_name, ctx.channels);
    if (!channel) continue;
    const t = new Date(row.timestamp).getTime();
    if (Number.isNaN(t)) continue;
    (byTs[channel] ??= new Map()).set(t, value(row));
  }
  const byChannel: Record<string, SeriesPoint[]> = {};
  const latest: Record<string, number> = {};
  for (const key of Object.keys(byTs)) {
    const points = Array.from(byTs[key], ([t, v]) => ({ t, v })).sort(
      (a, b) => a.t - b.t,
    );
    byChannel[key] = points;
    const last = points[points.length - 1];
    if (last) latest[key] = last.v;
  }
  return { byChannel, latest, reset: true, loading: false };
}

export class DorisSeriesProvider implements SeriesProvider {
  readonly mode = 'doris' as const;

  open(ctx: ProviderContext, range: ResolvedRange, onPatch: SeriesPatchHandler): SeriesSubscription {
    if (range.kind !== 'window') {
      onPatch({ byChannel: {}, loading: false });
      return { close() {} };
    }
    let cancelled = false;
    void (async () => {
      onPatch({ byChannel: {}, loading: true });
      try {
        if (!ctx.machineBK) {
          onPatch({ byChannel: {}, loading: false });
          return;
        }
        const start = new Date(range.startMs).toISOString();
        const end = new Date(range.endMs).toISOString();
        const rows: SensorHistoryRow[] = [];
        for (const prefix of dorisPrefixes(ctx.channels)) {
          const part = await query<SensorHistoryRow>(StatementIds.SENSOR_HISTORY, {
            serialno: ctx.machineBK,
            sensor_prefix: `${prefix}%`,
            start,
            end,
          });
          // Loop rather than `push(...part)` — see loadDetail for why.
          for (const row of part) rows.push(row);
        }
        if (!cancelled) onPatch(group(rows, (r) => r.avg_value, ctx));
      } catch (err) {
        console.error('DorisSeriesProvider: history failed', err);
        if (!cancelled) onPatch({ byChannel: {}, loading: false });
      }
    })();
    return {
      close() {
        cancelled = true;
      },
    };
  }

  async loadDetail(ctx: ProviderContext, startMs: number, endMs: number): Promise<SeriesPatch> {
    const start = new Date(startMs).toISOString();
    const end = new Date(endMs).toISOString();
    const rows: SensorDetailRow[] = [];
    for (const prefix of dorisPrefixes(ctx.channels)) {
      const part = await query<SensorDetailRow>(StatementIds.SENSOR_HISTORY_DETAIL, {
        serialno: ctx.machineBK,
        sensor_prefix: `${prefix}%`,
        start,
        end,
      });
      // Append with a loop, NOT `push(...part)`: spreading passes every row as a
      // separate argument, and full-resolution detail for `%` (every sensor on
      // the machine) is easily past the engine's argument limit — which throws
      // "Maximum call stack size exceeded".
      for (const row of part) rows.push(row);
    }
    return group(rows, (r) => r.value, ctx);
  }
}
